import { useEffect, useState } from 'react';
import type { BriefInput, ExtractionResult, ValidationIssue } from '../types';
import { Card, Field, Notice, Section, TextArea, cx } from '../components/ui/primitives';
import { emptyBriefInput } from '../state/draft';
import { clearBriefInput, saveBriefInput } from '../state/briefInputStorage';
import { AttachmentsSection } from '../components/review/AttachmentsSection';

/**
 * The brief-entry form.
 *
 * The brief itself comes first, because it is the input everything else is read
 * out of: brand, agency, channel, cohorts, audience sizes and signals are all
 * extracted from the pasted text (or an attachment) rather than typed.
 *
 * The only thing asked for is the workflow type, which is the one fact a brief
 * cannot be trusted to state — an agency brief and a direct-brand brief look
 * alike, and the choice decides which Drive folder the document lands in.
 *
 * Everything extraction misses is corrected on Review & Edit, which is why this
 * screen never blocks: an unreadable brief must still be able to reach the
 * screen where it can be fixed by hand.
 */

const BUSINESS_TYPES: Array<{ value: BriefInput['businessType']; label: string; help: string }> = [
  { value: 'AGENCY', label: 'Via Agency', help: 'The brief comes through a media agency.' },
  { value: 'DIRECT', label: 'Direct Brand', help: 'The brand is working with us directly.' },
];

/**
 * True when this screen has what it needs to continue.
 *
 * Nothing is mandatory here any more. Brand, agency and channel are all
 * extracted, and the workflow type always holds a value, so the only way to
 * stall would be to block on something the operator cannot see yet. Review &
 * Edit is where missing values are reported and fixed.
 */
export function isBriefInputComplete(_input: BriefInput): boolean {
  return true;
}

export interface BriefFormHandle {
  input: BriefInput;
  /** Extraction results for the files the user attached. */
  attachments: ExtractionResult[];
  /** Reveals validation messages and returns whether the form is usable. */
  attemptContinue: () => boolean;
  clear: () => void;
}

export function BriefForm({
  initial,
  serverIssues,
  onReady,
}: {
  initial: BriefInput;
  serverIssues: ValidationIssue[];
  /** Hands the parent a stable way to read and act on the form. */
  onReady: (handle: BriefFormHandle) => void;
}) {
  const [input, setInput] = useState<BriefInput>(initial);
  /*
   * Attachments are held here rather than in `input` because they are not part
   * of the persisted brief text — an extraction result is large, and re-reading
   * a stale one after a sign-in redirect would be worse than asking again.
   */
  const [attachments, setAttachments] = useState<ExtractionResult[]>([]);

  const patch = (changes: Partial<BriefInput>) =>
    setInput((current) => ({ ...current, ...changes }));

  // Survive the full-page redirect that Google sign-in performs.
  useEffect(() => {
    saveBriefInput(input);
  }, [input]);

  /*
   * There is nothing to validate locally: every field on this screen either has
   * a value already or is read out of the brief. Server-side issues are still
   * rendered below, so a rejected request is never silent.
   */

  // Republish the handle whenever the readable state changes.
  useEffect(() => {
    onReady({
      input,
      attachments,
      attemptContinue: () => isBriefInputComplete(input),
      clear: () => {
        setInput(emptyBriefInput());
        setAttachments([]);
        clearBriefInput();
      },
    });
  }, [input, attachments, onReady]);

  /** Appends OCR'd or extracted text to the brief box, rather than replacing it. */
  const appendToBrief = (text: string) => {
    setInput((current) => ({
      ...current,
      previousBrief: current.previousBrief.trim()
        ? `${current.previousBrief.trimEnd()}\n\n${text}`
        : text,
    }));
  };

  return (
    <Card className="p-5 sm:p-7">
      <div className="space-y-8">
        {/*
          The brief leads. Brand, agency, cohorts, sizes and commercials are all
          read out of it, so asking for it first matches the actual data flow.
        */}
        <Section title="Audience Push Details">
          <Field
            label="Paste the answered brief"
            htmlFor="input-brief"
            help="Brand, agency, channel, ad account, cohorts, audience sizes and signals are all read out of this automatically. Any format works — a pasted table from Excel or Google Docs, a list of Label: value lines, or plain text. Nothing is inferred that is not written here."
          >
            <TextArea
              id="input-brief"
              rows={14}
              value={input.previousBrief}
              placeholder={
                'Paste the previously answered brief here, for example:\n\n' +
                'Company Name | Hyundai\n' +
                'Agency Name | Innocean Media\n' +
                'Campaign Channel | META\n' +
                'Audience Cohort - 1 | - Age: 25-45, Male, Working Professionals\n' +
                'Audience Size | 18 - 20 mn\n' +
                'RO Value | Rs. 2,20,000'
              }
              onChange={(event) => patch({ previousBrief: event.target.value })}
              className="font-mono text-[13px] leading-6"
            />
          </Field>
        </Section>

        <AttachmentsSection
          attachments={attachments}
          onChange={setAttachments}
          onUseText={appendToBrief}
        />

        {/*
          The one thing the brief cannot be trusted to state. The channel used to
          be chosen here too; it is now read from the brief and corrected on
          Review & Edit like every other extracted value.
        */}
        <Section title="Workflow" hint="— not read from the brief">
          <div
            role="radiogroup"
            aria-label="Business Type"
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {BUSINESS_TYPES.map((option) => {
              const selected = input.businessType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => patch({ businessType: option.value })}
                  className={cx(
                    'rounded-input border px-4 py-3.5 text-left transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-accent/40',
                    selected
                      ? 'border-accent bg-accent-soft'
                      : 'border-line bg-surface hover:border-line-strong',
                  )}
                >
                  <span className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className={cx(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                        selected ? 'border-accent' : 'border-line-strong',
                      )}
                    >
                      {selected ? <span className="h-2 w-2 rounded-full bg-accent" /> : null}
                    </span>
                    <span className="text-field font-medium text-ink">{option.label}</span>
                  </span>
                  <span className="mt-1 block pl-[26px] text-micro text-ink-muted">
                    {option.help}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Additional Information" hint="(optional)">
          <Field label="Anything the brief did not cover" htmlFor="input-additional">
            <TextArea
              id="input-additional"
              rows={3}
              value={input.additionalInfo}
              placeholder="e.g. Exclusions, frequency caps, creative notes, or context the ops team should know."
              onChange={(event) => patch({ additionalInfo: event.target.value })}
            />
          </Field>
        </Section>

        {serverIssues.length ? (
          <Notice tone="error" title="Some required information is missing">
            <ul className="list-disc space-y-0.5 pl-4">
              {serverIssues.map((issue) => (
                <li key={`${issue.path}-${issue.message}`}>{issue.message}</li>
              ))}
            </ul>
          </Notice>
        ) : null}
      </div>
    </Card>
  );
}
